import { useContext, useState } from 'react'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { PixelFactoryContext } from 'pixel-base'
import queryString from 'query-string'
import { AjaxResponse } from 'rxjs/ajax'

import {
  get,
  getCurrencyList,
  getOptionsSettings,
  getTimezones,
  updateCompany,
  updateOptions,
} from 'utils/api-routes.util'
import history from 'utils/history'

import {
  IAdminSettings,
  IAllowedCountry,
  ICompunknownInfo,
  ICurrency,
  IOnBoardingContextProps,
  IPixelFactory,
  IStates,
} from '../../decl'

export const OnBoardingContext = React.createContext(
  {} as IOnBoardingContextProps,
)

export const useOnBoardingContext = () => {
  const context = useContext(OnBoardingContext)
  const pixelFactory = useContext<IPixelFactory>(PixelFactoryContext)

  if (!context) {
    throw new Error(
      'useOnBoardingContext must be used within a OnBoardingContextProvider',
    )
  }
  return { ...context, pixelFactory }
}

export const OnBoardingProvider = React.memo(
  ({ children }: { children: React.ReactNode }) => {
    const [adminSettings, setAdminSettings] = useState<IAdminSettings>(
      {} as IAdminSettings,
    )
    const [currencyList, setCurrencyList] = useState<ICurrency>({} as ICurrency)
    const [timezones, setTimezones] = useState<string[]>([])
    const [countries, setCountries] = useState<IAllowedCountry[]>([])
    const [countryStates, setCountryStates] = useState<IStates>({} as IStates)
    const [activeStep, setActiveStep] = useState(0)

    const pixelFactory = React.useContext<IPixelFactory>(PixelFactoryContext)
    const [compunknownInfo, setCompunknownInfo] = useState<ICompunknownInfo>(
      {} as ICompunknownInfo,
    )
    const [companyError] = useState<boolean>(false)
    const [_error, setError] = React.useState({
      error: '',
      field: '',
    })
    const navigate = useNavigate()
    React.useEffect(() => {
      const params = queryString.parse(history.location.search)
      const currentStep = Number(params['step'])
      if (currentStep !== activeStep) {
        params['step'] = String(activeStep)
        const searchString = queryString.stringify(params)
        history.push(`${history.location.pathname}?${searchString}`)
      }
    }, [activeStep])

    const getAdminSettings = () => {
      pixelFactory.showPageLoader()
      getOptionsSettings({}).subscribe((res) => {
        pixelFactory.hidePageLoader()
        setAdminSettings(
          (res as AjaxResponse<{ data: IAdminSettings }>).response.data,
        )
      })
    }

    const getAllCountries = () => {
      pixelFactory.showPageLoader()
      get('/settings/options/countries').subscribe((res) => {
        pixelFactory.hidePageLoader()
        const response = res as AjaxResponse<{ data: IAllowedCountry[] }>
        setCountries(response.response.data)
        setCountryStates({} as IStates)
      })
    }

    const getAllCurrencyList = () => {
      pixelFactory.showPageLoader()
      getCurrencyList({}).subscribe((res) => {
        const response = res as AjaxResponse<{ data: ICurrency }>
        pixelFactory.hidePageLoader()
        setCurrencyList(response.response.data)
      })
    }

    const getAllTimezones = () => {
      pixelFactory.showPageLoader()
      getTimezones({}).subscribe((res) => {
        pixelFactory.hidePageLoader()
        const response = res as AjaxResponse<{ data: string[] }>
        setTimezones(response.response.data)
      })
    }

    const updateAllOptions = () => {
      pixelFactory.showPageLoader()
      updateOptions({}).subscribe((res) => {
        pixelFactory.hidePageLoader()
        const response = res as AjaxResponse<{ data: IAdminSettings }>
        if (response.response.data) {
          history.push('/admin/couriers')
          setAdminSettings(response.response.data)
          navigate('/orders')
        }
      })
    }

    const getCompunknownInfo = () => {
      get('/tenant/company').subscribe((res) => {
        const response = res as AjaxResponse<{ data: ICompunknownInfo }>
        setCompunknownInfo(response.response.data)
      })
    }

    const updateCompunknownInfo = (data?: ICompunknownInfo) => {
      pixelFactory.showPageLoader()
      const payload = {
        company_name: data?.compunknown_name,
        company_phone_number: data?.compunknown_phone_number,
        company_website: data?.compunknown_website,
        company_email: data?.compunknown_email,
        company_address: data?.compunknown_address,
        company_city: data?.compunknown_city,
        company_country: data?.compunknown_country,
        company_zip: data?.compunknown_zip,
        company_state: data?.compunknown_state,
        company_whatsapp_number: data?.whatsapp_number,
      }
      updateCompany(payload).subscribe((res) => {
        const response = res as AjaxResponse<{
          data: ICompunknownInfo
          error?: string
          field?: string
        }>
        pixelFactory.hidePageLoader()
        if (!response.response.error) {
          history.push('/dashboard')
        } else {
          if (response.response.error && response.response.field) {
            setError({
              error: response.response.error,
              field: response.response.field,
            })
          } else {
            pixelFactory.showFailure(response.response.error)
          }
        }
        navigate('/orders')
      })
    }

    const defaultContext: IOnBoardingContextProps = {
      adminSettings,
      currencyList,
      timezones,
      countries,
      countryStates,
      activeStep,
      compunknownInfo,
      getAdminSettings,
      getAllCurrencyList,
      getAllTimezones,
      updateAllOptions,
      getCompunknownInfo,
      updateCompunknownInfo,
      setActiveStep,
      compunknownError: companyError,
      history: {
        ...history,
        scrollRestoration: 'auto',
        state: {},
        pushState: (_data: any, _unused: string, url?: string | URL | null) => {
          if (url) {
            history.push(url.toString())
          }
        },
        replaceState: (
          _data: any,
          _unused: string,
          url?: string | URL | null,
        ) => {
          if (url) {
            history.replace(url.toString())
          }
        },
      } as any,
    }

    return (
      <OnBoardingContext.Provider value={defaultContext}>
        {children}
      </OnBoardingContext.Provider>
    )
  },
)

OnBoardingProvider.displayName = 'OnBoardingProvider'

OnBoardingContext.displayName = 'OnBoardingContext'
